<template>
  <div
    ref="modalEl"
    class="modal fade"
    id="openingBalanceModal"
    tabindex="-1"
  >
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content modal-rounded">
        <div class="modal-header">
          <div>
            <h5 class="modal-title">
              {{
                isCorrectionMode
                  ? "Correct Opening Balance"
                  : "Set Opening Balance"
              }}
            </h5>

            <div class="text-muted small">
              Financial year:
              <strong>
                {{ financialYear?.year_key || "Not selected" }}
              </strong>
            </div>
          </div>

          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            :disabled="saving"
          ></button>
        </div>

        <div class="modal-body">
          <div
            v-if="isFinancialYearClosed"
            class="alert alert-light border py-2"
          >
            <i class="bi bi-info-circle me-1"></i>
            This financial year is closed. The opening balance is read-only.
          </div>

          <div
            v-else-if="isInactiveYear"
            class="alert alert-warning border py-2"
          >
            <i class="bi bi-exclamation-circle me-1"></i>
            This financial year is inactive, but it is not closed. You may still
            enter setup data such as opening balances.
          </div>

          <div
            v-else-if="!financialYear?.id"
            class="alert alert-warning border py-2"
          >
            <i class="bi bi-exclamation-circle me-1"></i>
            Select a financial year before setting an opening balance.
          </div>

          <div
            v-if="error"
            class="alert alert-danger py-2 mb-3"
          >
            <i class="bi bi-exclamation-triangle me-1"></i>
            {{ error }}
          </div>

          <div
            v-if="loadingExisting"
            class="alert alert-light border py-2 mb-3 small"
          >
            <span class="spinner-border spinner-border-sm me-2"></span>
            Checking existing opening balance...
          </div>

          <div
            v-if="existingBalance"
            class="alert alert-info py-2 mb-3 small"
          >
            <div class="fw-semibold">
              Existing opening balance found for
              {{ form.as_of_period }}
            </div>

            <div>
              Current amount:
              <strong>{{ fmt(existingBalance.amount) }} RWF</strong>

              <span
                v-if="existingBalance.source_type"
                class="ms-2"
              >
                Source:
                <strong>{{ existingBalance.source_type }}</strong>
              </span>
            </div>

            <div
              v-if="existingBalance.notes"
              class="mt-1"
            >
              Notes: {{ existingBalance.notes }}
            </div>
          </div>

          <div class="row g-2">
            <div class="col-md-6">
              <label class="form-label">Member</label>

              <div
                v-if="lockMember"
                class="border rounded p-2 bg-light"
              >
                <div class="fw-semibold">
                  {{ member?.name || "—" }}
                </div>

                <div class="text-muted small">
                  {{ member?.phone || member?.email || "—" }}
                </div>
              </div>

              <select
                v-else
                v-model="form.user_id"
                class="form-select"
                :disabled="
                  saving ||
                  loadingExisting ||
                  !isWritableYear
                "
                @change="onMemberOrPeriodChanged"
              >
                <option value="">Select member</option>

                <option
                  v-for="row in members"
                  :key="row.id"
                  :value="row.id"
                >
                  {{ row.name }} ({{ row.phone || "-" }})
                </option>
              </select>
            </div>

            <div class="col-md-6">
              <label class="form-label">
                Opening balance amount
              </label>

              <input
                v-model.number="form.amount"
                type="number"
                step="0.01"
                class="form-control"
                placeholder="0"
                :disabled="
                  saving ||
                  loadingExisting ||
                  !isWritableYear
                "
              />

              <div class="text-muted small mt-1">
                Negative values are allowed for carried-forward deficits.
              </div>
            </div>

            <div class="col-md-6">
              <label class="form-label">
                As-of period
              </label>

              <input
                v-model="form.as_of_period"
                type="month"
                class="form-control"
                :min="yearStartPeriod || undefined"
                :max="yearEndPeriod || undefined"
                :disabled="
                  saving ||
                  loadingExisting ||
                  !isWritableYear
                "
                @change="onMemberOrPeriodChanged"
              />

              <div class="text-muted small mt-1">
                Saved as
                <strong>{{ form.as_of_period || "—" }}</strong>
                (YYYY-MM)
              </div>
            </div>

            <div class="col-md-6">
              <label class="form-label">
                Source type
              </label>

              <select
                v-model="form.source_type"
                class="form-select"
                :disabled="
                  saving ||
                  loadingExisting ||
                  !isWritableYear
                "
              >
                <option value="manual">
                  Manual first setup
                </option>
                <option value="imported">
                  Imported from old system
                </option>
                <option value="carry_forward">
                  Carry forward
                </option>
                <option value="correction">
                  Correction
                </option>
              </select>
            </div>

            <div class="col-md-6">
              <label class="form-label">
                Source reference
              </label>

              <input
                v-model.trim="form.source_reference"
                type="text"
                class="form-control"
                placeholder="e.g. FY 2026 closing"
                :disabled="
                  saving ||
                  loadingExisting ||
                  !isWritableYear
                "
              />

              <div
                v-if="form.source_type === 'carry_forward'"
                class="text-muted small mt-1"
              >
                Required for carry-forward balances.
              </div>
            </div>

            <div
              v-if="existingBalance"
              class="col-md-6"
            >
              <label class="form-label">
                Difference
              </label>

              <div
                class="form-control bg-light fw-semibold"
                :class="
                  amountDifference === 0
                    ? 'text-muted'
                    : amountDifference > 0
                      ? 'text-success'
                      : 'text-danger'
                "
              >
                {{ amountDifference > 0 ? "+" : "" }}
                {{ fmt(amountDifference) }} RWF
              </div>
            </div>

            <div class="col-md-12">
              <label class="form-label">
                {{
                  isCorrectionMode
                    ? "Correction reason / Notes"
                    : "Notes"
                }}
              </label>

              <textarea
                v-model.trim="form.notes"
                class="form-control"
                rows="3"
                :placeholder="
                  isCorrectionMode
                    ? 'Explain why this balance is being changed...'
                    : 'e.g. Imported from old system ledger'
                "
                :disabled="
                  saving ||
                  loadingExisting ||
                  !isWritableYear
                "
              ></textarea>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button
            class="btn btn-outline-secondary"
            data-bs-dismiss="modal"
            :disabled="saving"
          >
            Close
          </button>

          <button
            v-if="isWritableYear"
            class="btn btn-primary"
            :disabled="
              saving ||
              loadingExisting ||
              !canSave
            "
            @click="save"
          >
            <span
              v-if="saving"
              class="spinner-border spinner-border-sm me-2"
            ></span>

            {{
              saving
                ? "Saving..."
                : isCorrectionMode
                  ? "Save Correction"
                  : "Save Opening Balance"
            }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import http from "@/services/http";
import { Modal } from "bootstrap";

export default {
  name: "OpeningBalanceModal",

  props: {
    members: {
      type: Array,
      default: () => [],
    },

    financialYear: {
      type: Object,
      default: null,
    },

    memberFinancialYear: {
      type: Object,
      default: null,
    },

    financialYearClosure: {
      type: Object,
      default: null,
    },
  },

  emits: ["saved"],

  data() {
    return {
      modal: null,
      existingTimer: null,

      saving: false,
      loadingExisting: false,
      error: "",

      lockMember: false,
      member: null,
      existingBalance: null,

      form: {
        financial_year_rule_id: "",
        user_id: "",
        amount: "",
        as_of_period: "",
        source_type: "manual",
        source_reference: "",
        notes: "",
      },
    };
  },

  computed: {
    isFinancialYearClosed() {
      return Boolean(
        this.memberFinancialYear?.closed_at
        || this.financialYearClosure?.status === "posted"
        || this.financialYearClosure?.posted_at
      );
    },

    isWritableYear() {
      return Boolean(
        this.financialYear?.id
        && !this.isFinancialYearClosed
      );
    },

    isInactiveYear() {
      return Boolean(
        this.financialYear?.id
        && !this.financialYear?.is_active
        && !this.isFinancialYearClosed
      );
    },

    selectedAmount() {
      if (
        this.form.amount === ""
        || this.form.amount === null
      ) {
        return null;
      }

      return Number(this.form.amount);
    },

    isCorrectionMode() {
      return (
        Boolean(this.existingBalance)
        || this.form.source_type === "correction"
      );
    },

    amountDifference() {
      if (!this.existingBalance) return 0;

      const oldAmount = Number(
        this.existingBalance.amount || 0
      );
      const newAmount = Number(
        this.form.amount || 0
      );

      return Math.round(
        (newAmount - oldAmount) * 100
      ) / 100;
    },

    canSave() {
      if (!this.isWritableYear) return false;
      if (!this.form.financial_year_rule_id) return false;
      if (!this.form.user_id) return false;
      if (!this.form.as_of_period) return false;

      if (
        this.selectedAmount === null
        || Number.isNaN(this.selectedAmount)
      ) {
        return false;
      }

      if (
        this.form.source_type === "carry_forward"
        && !String(
          this.form.source_reference || ""
        ).trim()
      ) {
        return false;
      }

      if (
        this.isCorrectionMode
        && String(this.form.notes || "").trim().length < 3
      ) {
        return false;
      }

      return true;
    },

    yearStartPeriod() {
      return this.financialYear?.start_date
        ? String(this.financialYear.start_date).slice(0, 7)
        : "";
    },

    yearEndPeriod() {
      return this.financialYear?.end_date
        ? String(this.financialYear.end_date).slice(0, 7)
        : "";
    },

    defaultPeriod() {
      if (this.yearStartPeriod) {
        return this.yearStartPeriod;
      }

      return this.monthKeyToday();
    },
  },

  watch: {
    "form.as_of_period"() {
      this.onMemberOrPeriodChanged();
    },

    financialYear: {
      deep: true,
      immediate: true,
      handler() {
        this.form.financial_year_rule_id =
          this.financialYear?.id || "";

        if (
          this.financialYear?.id
          && (
            !this.form.as_of_period
            || this.form.as_of_period < this.yearStartPeriod
            || this.form.as_of_period > this.yearEndPeriod
          )
        ) {
          this.form.as_of_period = this.defaultPeriod;
        }

      },
    },
  },

  methods: {
    fmt(value) {
      return Number(value || 0).toLocaleString(
        undefined,
        {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }
      );
    },

    monthKeyToday() {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(
        today.getMonth() + 1
      ).padStart(2, "0");

      return `${year}-${month}`;
    },

    resetForm(period = "") {
      this.error = "";
      this.saving = false;
      this.loadingExisting = false;
      this.existingBalance = null;

      this.form = {
        financial_year_rule_id:
          this.financialYear?.id || "",
        user_id: "",
        amount: "",
        as_of_period:
          period || this.defaultPeriod,
        source_type: "manual",
        source_reference: "",
        notes: "",
      };
    },

    openForMember(user, options = {}) {
      const normalized =
        typeof options === "string"
          ? { period: options }
          : options || {};

      this.resetForm(
        normalized.period || ""
      );

      if (normalized.financialYear?.id) {
        this.form.financial_year_rule_id =
          normalized.financialYear.id;
      }

      this.lockMember = true;
      this.member = user || null;
      this.form.user_id = user?.id || "";

      this.modal?.show();

      this.$nextTick(async () => {
        await this.loadExisting();
      });
    },

    openBlank(options = {}) {
      const normalized =
        typeof options === "string"
          ? { period: options }
          : options || {};

      this.resetForm(
        normalized.period || ""
      );

      if (normalized.financialYear?.id) {
        this.form.financial_year_rule_id =
          normalized.financialYear.id;
      }

      this.lockMember = false;
      this.member = null;

      this.modal?.show();

    },

    onMemberOrPeriodChanged() {
      if (
        !this.form.user_id
        || !this.form.as_of_period
        || !this.form.financial_year_rule_id
      ) {
        return;
      }

      clearTimeout(this.existingTimer);

      this.existingTimer = setTimeout(
        () => this.loadExisting(),
        250
      );
    },

    async loadExisting() {
      if (
        !this.form.user_id
        || !this.form.as_of_period
        || !this.form.financial_year_rule_id
      ) {
        return;
      }

      this.loadingExisting = true;
      this.error = "";

      try {
        const response = await http.get(
          `/opening-balances/user/${this.form.user_id}`,
          {
            params: {
              financial_year_rule_id:
                this.form.financial_year_rule_id,
              as_of_period:
                this.form.as_of_period,
            },
          }
        );

        const row =
          response.data?.data || null;

        this.existingBalance = row;

        if (row) {
          this.form.amount = Number(
            row.amount || 0
          );
          this.form.source_type = "correction";
          this.form.source_reference =
            row.source_reference || "";
          this.form.notes =
            row.notes || row.note || "";
        } else {
          this.form.amount = "";
          this.form.source_type = "manual";
          this.form.source_reference = "";
          this.form.notes = "";
        }
      } catch (error) {
        this.existingBalance = null;

        if (
          error?.response?.status
          && error.response.status !== 404
        ) {
          const errors =
            error?.response?.data?.errors;

          const firstError = errors
            ? Object.values(errors)?.[0]?.[0]
            : null;

          this.error =
            firstError
            || error?.response?.data?.message
            || "Failed to check existing opening balance.";
        }
      } finally {
        this.loadingExisting = false;
      }
    },

    async save() {
      if (!this.canSave) return;

      this.saving = true;
      this.error = "";

      try {
        const payload = {
          financial_year_rule_id:
            Number(this.form.financial_year_rule_id),
          user_id: this.form.user_id,
          amount: this.selectedAmount,
          as_of_period:
            this.form.as_of_period,
          source_type:
            this.form.source_type || "manual",
          source_reference:
            String(
              this.form.source_reference || ""
            ).trim() || null,
          notes:
            String(
              this.form.notes || ""
            ).trim() || null,
        };

        const response = await http.post(
          "/opening-balances",
          payload
        );

        this.modal?.hide();

        this.$emit(
          "saved",
          response.data?.data || null
        );
      } catch (error) {
        const errors =
          error?.response?.data?.errors;

        const firstError = errors
          ? Object.values(errors)?.[0]?.[0]
          : null;

        this.error =
          firstError
          || error?.response?.data?.message
          || error?.message
          || "Failed to save opening balance.";
      } finally {
        this.saving = false;
      }
    },
  },

  mounted() {
    const element = this.$refs.modalEl;

    if (!element) return;

    this.modal =
      Modal.getOrCreateInstance(element);

    element.addEventListener(
      "hidden.bs.modal",
      () => {
        this.error = "";
        this.saving = false;
        this.loadingExisting = false;
        clearTimeout(this.existingTimer);
      }
    );
  },

  beforeUnmount() {
    clearTimeout(this.existingTimer);
  },
};
</script>

<style scoped>
.modal-rounded {
  border-radius: 1rem;
  overflow: hidden;
}

</style>
